import React, { Fragment, useEffect, useCallback, useState } from 'react';
import { Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { PageAnimation } from '../../Layout/Components/Animation';
import PageTitle from '../../Layout/Components/PageTitle';
import { useAuth } from '../../Context/Auth/AuthContext';
import { toast } from 'react-toastify';
import Strings from '../../translations/Translations';
import CardButton from '../../Layout/Components/CardButton';
import PSO from '../../services/PSO';
import { Link, useHistory } from 'react-router-dom';
import AppleStoreImage from '../../assets/utils/images/apple_pp_store-267x89.png';
import PlayStoreImage from '../../assets/utils/images/google-play-badge-300x89.png';
import WebAppImage from '../../assets/utils/images/banner_popfans_web.png';

const REMIX_APP_IOS_LINK = process.env.REACT_APP_REMIX_APP_IOS_LINK;
const REMIX_APP_ANDROID_LINK = process.env.REACT_APP_REMIX_APP_ANDROID_LINK;
const POPFANS_APP_IOS_LINK = process.env.REACT_APP_POPFANS_APP_IOS_LINK;
const POPFANS_APP_ANDROID_LINK = process.env.REACT_APP_POPFANS_APP_ANDROID_LINK;
const REMIX_APP_WEB = process.env.REACT_APP_REMIX_APP_WEB;
const POPFANS_APP_WEB = process.env.REACT_APP_POPFANS_APP_WEB;

export default function Dashboard() {
  const history = useHistory();
  const { clearActiveCampaign, authenticated, toggleLoading, toggleCurrentCampaign, toggleActiveCampaign } = useAuth();

  const [campaignsData, setCampaignsData] = useState([]);

  useEffect(() => {
    clearActiveCampaign();
  }, [clearActiveCampaign]);

  const getCollections = useCallback(async () => {
    toggleLoading(true);

    try {
      const args = {
        un: authenticated.un,
        al: authenticated.al
      };
      const response = await PSO.getCollectionListByOwner(args);

      if (response.result === 'success' && response.its) {
        if (response.its.length > 6) {
          setCampaignsData(response.its.slice(0, 6));
        } else {
          setCampaignsData(response.its);
        }
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }

      toggleLoading(false);
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [authenticated, toggleLoading, setCampaignsData]);

  useEffect(() => {
    getCollections();
  }, [getCollections]);

  const getCollectionPars = useCallback(async (colid) => {
    let result = false;
    toggleLoading(true);

    try {
      const response = await PSO.getCollectionPars({
        un: authenticated.un,
        al: authenticated.al,
        colid
      });

      if (response.result === 'success' && response.its && response.its.length > 0) {
        result = response.its[0];
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);
    return result;
  }, [authenticated, toggleLoading]);

  const goToCampaign = useCallback(async (item) => {
    if (item && item.colid) {
      const collectionPars = await getCollectionPars(item.colid);
      if (collectionPars) {
        toggleCurrentCampaign(collectionPars);
        history.replace('/campaign');
        toggleActiveCampaign(true);
      }
    }
  }, [history, toggleActiveCampaign, toggleCurrentCampaign, getCollectionPars]);

  return (
    <Fragment>
      <PageAnimation>
        <PageTitle
          heading={Strings('dashboard')}
          subheading={Strings('application_title')}
          icon='pe-7s-home'
        />

        <Row className='mb-3'>
          <Col md={12}>
            <h4>{Strings('welcome_to_pepblast_campaign_manager')}</h4>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col sm='12' md='6' xl='6'>
            <CardButton
              title={Strings('create_campaign')}
              buttonLabel={Strings('create')}
              buttonLink='/campaigns/create'
            />
          </Col>

          <Col sm='12' md='6' xl='6'>
            <CardButton
              title={Strings('create_new_video')}
              buttonLabel={Strings('create')}
              buttonLink='/create-video'
            />
          </Col>
          
        </Row>

        <div>
          {campaignsData && campaignsData.length > 0 && (
            <Fragment>
              <Row className='mb-2'>
                <Col md={12}>
                  <h5>{Strings('your_last_campaigns')}</h5>
                </Col>
              </Row>

              <Row>
                {campaignsData && campaignsData.length > 0 && campaignsData.map((value) => (
                  <Col md={4} key={value.colid}>
                    <Card
                      className="card-shadow-primary mb-3 widget-chart widget-chart2 text-left pointer"
                      onClick={() => goToCampaign(value)}
                    >
                      <div className="widget-content p-0 w-100">
                        <div className="widget-content-outer">
                          <div className="widget-content-wrapper">
                            <div className="widget-content-left pr-2 fsize-1">
                              <div className="widget-numbers mt-0 fsize-1">
                                {value.coln}
                              </div>
                            </div>
                          </div>
                          <div className="widget-content-left fsize-1">
                            <div className="text-muted opacity-6">
                              {value.cold}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
              <Row className='mb-4'>
                <Col md={12} className='text-right'>
                  <Link
                    className='btn btn-primary'
                    to='/campaigns'
                  >
                    {Strings('all_campaigns')}
                  </Link>
                </Col>
              </Row>
            </Fragment>
          )}
        </div>

        <Row className='mb-4'>
          <Col>
            <Card>
              <CardHeader>
                {Strings('about_the_pcm')}
              </CardHeader>
              <CardBody>
                {Strings('dashboard_intro_text')}

                <h5 className='mt-4'>{Strings('popfans_app')}</h5>
                <p>
                  <a href={POPFANS_APP_IOS_LINK} target="_blank" rel="noopener noreferrer">
                    <img src={AppleStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_ios')} />
                  </a>

                  <a href={POPFANS_APP_ANDROID_LINK} target="_blank" rel="noopener noreferrer">
                    <img src={PlayStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_android')} />
                  </a>

                  <a href={POPFANS_APP_WEB} target="_blank" rel="noopener noreferrer">
                    <img src={WebAppImage} className="get_app_image_button" alt={Strings('access_remix_app_for_web')} />
                  </a>
                </p>

                <h5 className='mt-4'>{Strings('pepblast_remix_app')}</h5>
                <p>
                  <a href={REMIX_APP_IOS_LINK} target="_blank" rel="noopener noreferrer">
                    <img src={AppleStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_ios')} />
                  </a>

                  <a href={REMIX_APP_ANDROID_LINK} target="_blank" rel="noopener noreferrer">
                    <img src={PlayStoreImage} className="mr-2 get_app_image_button" alt={Strings('download_remix_app_for_android')} />
                  </a>

                  <a href={REMIX_APP_WEB} target="_blank" rel="noopener noreferrer">
                    <img src={WebAppImage} className="get_app_image_button" alt={Strings('access_popfans_app_for_web')} />
                  </a>
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </PageAnimation>
    </Fragment>
  );
}
